import { DynamicFieldType } from '@page-components/shipment/common';
import { Fragment, useMemo } from 'react';
import { DeleteIcon, StyledTable } from '../styles/common';
import { Field } from './Field';
import type { ShipmentDynamicFieldType } from '@hooks/shipment';
import {
  AppTableHeader,
  AppTableRow,
  AppTableHeaderCell,
  AppTableBody,
  AppTableCell,
} from '@components/AppTable';

type NestedTablePropsType = {
  fields?: ShipmentDynamicFieldType[];
  rows: any[];
  errors: Record<string, any>[];
  onChange: (value: any) => void;
  onPressEnter?: () => void;
  removeNestedField: (index: number) => void;
  allowActions?: boolean;
};

export function NestedTable({
  allowActions,
  errors,
  fields,
  onChange,
  onPressEnter,
  removeNestedField,
  rows,
}: NestedTablePropsType) {
  const nestedOnChange = (name, value, index) => {
    const temp = rows.map((item, rIndex) => {
      if (index === rIndex) {
        return {
          ...item,
          [name]: value,
        };
      }
      return item;
    });
    onChange(temp);
  };

  const dimensionsUnit = useMemo(
    () => fields?.find(x => x.name === 'DimensionUnit')?.value || 'cm',
    [fields],
  );
  const weightUnit = useMemo(
    () => fields?.find(x => x.name === 'WeightUnit')?.value || 'kg',
    [fields],
  );

  const getCommonProps = (index, fieldIndex) => ({
    onChange: (name, value) => nestedOnChange(name, value, index),
    hideLabel: true,
    index,
    onPressEnter:
      fields?.filter(x =>
        [
          DynamicFieldType.currency,
          DynamicFieldType.number,
          DynamicFieldType.text,
        ].includes(x.inputType),
      ).length ===
      fieldIndex + 1
        ? onPressEnter
        : undefined,
    style: true,
  });

  const filteredColumns = fields?.filter(
    x => x.inputType !== DynamicFieldType.hidden,
  );

  return (
    <StyledTable basic="very">
      <AppTableHeader>
        <AppTableRow>
          {filteredColumns?.map(
            item =>
              ((!!item.groupFields?.length &&
                item.groupFields?.findIndex(x => x === item.name) === 0) ||
                !item.groupFields) && (
                <AppTableHeaderCell key={item.name}>
                  {item.groupTitle || item.label}{' '}
                  {item.dimensionUnitValue &&
                    item.dimensionUnit &&
                    `(${dimensionsUnit})`}{' '}
                  {item.weightUnitValue &&
                    item.weightUnit &&
                    `(${weightUnit})`}{' '}
                </AppTableHeaderCell>
              ),
          )}
          {allowActions && <AppTableHeaderCell />}
        </AppTableRow>
      </AppTableHeader>
      <AppTableBody>
        {rows.map((row, index) => (
          <AppTableRow key={index}>
            {filteredColumns
              ?.filter(x =>
                x.groupFields?.length
                  ? x.groupFields?.findIndex(field => field === x.name) === 0
                  : true,
              )
              .map((itemField, i) => (
                <AppTableCell
                  className={
                    itemField.groupFields?.length
                      ? 'sk:flex sk:flex-wrap sk:-mx-4'
                      : itemField.layoutClass
                  }
                  key={itemField.name}
                >
                  <Field
                    key={itemField.name}
                    value={row[itemField.name]}
                    error={errors ? errors[index]?.[itemField.name] : null}
                    item={itemField}
                    layoutClass={
                      itemField.groupFields?.length ? itemField.layoutClass : ''
                    }
                    {...getCommonProps(index, i)}
                  />
                  {itemField.groupFields?.length ? (
                    fields
                      ?.filter(
                        x =>
                          !!x?.groupFields?.length &&
                          x?.groupFields?.findIndex(field => field === x.name) >
                            0,
                      )
                      .map(groupField => (
                        <Fragment key={groupField.name}>
                          <Field
                            key={groupField.name}
                            value={row[groupField.name]}
                            error={
                              errors ? errors[index]?.[groupField.name] : null
                            }
                            item={groupField}
                            layoutClass={groupField.layoutClass}
                            {...getCommonProps(index, i)}
                          />
                        </Fragment>
                      ))
                  ) : (
                    <></>
                  )}
                </AppTableCell>
              ))}
            {rows.length > 1 && allowActions && (
              <AppTableCell>
                <div
                  // @ts-expect-error - TS2322: Type '{ children: Element; type: string; size: string; className: string; onClic...
                  type="button"
                  size="mini"
                  className="sk:px-3.5!"
                  onClick={() => removeNestedField(index)}
                >
                  <DeleteIcon size={20} />
                </div>
              </AppTableCell>
            )}
          </AppTableRow>
        ))}
      </AppTableBody>
    </StyledTable>
  );
}
